import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPermissionList } from '#/api/system/permission';
import { fetchRoleList, fetchRolePermissions } from '#/api/system/role';
import { fetchUserAccess, replaceUserAccess } from '#/api/system/userAccess';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import UserPermissionOverrideEditor, {
  EFFECT_ALLOW,
  EFFECT_DENY,
} from '#/modules/infra/settings/access/components/UserPermissionOverrideEditor';
import { toast } from '#/utils/toast';

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function buildInitialOverrides(accessData) {
  const next = {};
  (accessData?.permissionOverrides ?? []).forEach((row) => {
    const permissionId = row.permission?.id || row.permissionId;
    if (permissionId && (row.effect === EFFECT_ALLOW || row.effect === EFFECT_DENY)) {
      next[permissionId] = row.effect;
    }
  });
  return next;
}

const UserAccessEditor = ({
  userId,
  accessData,
  roleOptions,
  catalog,
  catalogLoading,
  canWrite,
}) => {
  const queryClient = useQueryClient();
  const [selectedRoleIds, setSelectedRoleIds] = useState(
    (accessData.roles ?? []).map((role) => role.id).filter(Boolean),
  );
  const [overrides, setOverrides] = useState(() => buildInitialOverrides(accessData));

  const rolePermQueries = useQueries({
    queries: selectedRoleIds.map((roleId) => ({
      queryKey: ['system', 'role', 'permissions', roleId],
      queryFn: () => fetchRolePermissions(roleId),
    })),
  });

  const roleGrantedKey = rolePermQueries
    .map((query) => (query.data?.data ?? []).map((perm) => perm.id).join(','))
    .join('|');
  const roleGrantedIds = useMemo(
    () => new Set(roleGrantedKey.split(/[|,]/).filter(Boolean)),
    [roleGrantedKey],
  );
  const isRolePermLoading = rolePermQueries.some((query) => query.isLoading);

  const saveMutation = useMutation({
    mutationFn: () => replaceUserAccess(userId, {
      roleIds: selectedRoleIds,
      permissionOverrides: Object.entries(overrides)
        .map(([permissionId, effect]) => ({ permissionId, effect })),
    }),
    onSuccess: (result) => {
      toast.success(result?.message || '存取設定已儲存');
      queryClient.invalidateQueries({ queryKey: ['system', 'user', 'access', userId] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, '儲存失敗'));
    },
  });

  function toggleRole(roleId, checked) {
    setSelectedRoleIds((prev) => {
      if (checked) {
        return prev.includes(roleId) ? prev : [...prev, roleId];
      }
      return prev.filter((id) => id !== roleId);
    });
  }

  return (
    <form
      className="sheet create_edit create_edit_form create_edit_form--page-scroll"
      onSubmit={(event) => {
        event.preventDefault();
        saveMutation.mutate();
      }}
    >
      <div className="create_edit_form__body divide-y divide-gray-500">
        <section className="pb-6">
          <div className="form-block">
            <h2 className="form-block__title">角色</h2>
            <p className="form-block__description">勾選的角色權限會一次帶入下方。</p>
          </div>
          <ul className="grid grid-cols-1 gap-2 @xl/sheet:grid-cols-2 @4xl/sheet:grid-cols-3">
            {roleOptions.map((role) => {
              const checked = selectedRoleIds.includes(role.id);
              return (
                <li key={role.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 transition-colors',
                      checked ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:bg-primary-100/60',
                      !canWrite && 'cursor-not-allowed opacity-70',
                    )}
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={checked}
                      disabled={!canWrite}
                      onCheckedChange={(value) => toggleRole(role.id, value === true)}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm text-gray-800">{role.name || role.code}</span>
                      <span className="block truncate font-mono text-xs text-gray-500">{role.code}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>

        <section>
          <div className="form-block">
            <h2 className="form-block__title">功能權限</h2>
            <p className="form-block__description">
              預設沿用角色。需要例外時逐項設定額外允許或拒絕；額外允許建立、更新、審核等會自動補上讀取。
            </p>
          </div>
          {catalogLoading || isRolePermLoading ? (
            <p className="text-sm text-muted-foreground">載入權限中…</p>
          ) : (
            <UserPermissionOverrideEditor
              catalog={catalog}
              roleGrantedIds={roleGrantedIds}
              overrides={overrides}
              onChange={setOverrides}
              disabled={!canWrite}
            />
          )}
        </section>
      </div>

      <div className="create_edit_form__actions flex flex-wrap items-center justify-end gap-2">
        {!canWrite ? (
          <p className="mr-auto text-sm text-muted-foreground">
            唯讀：需要同時具備帳號與角色的更新權限才能儲存。
          </p>
        ) : null}
        <Button type="button" variant="outline" asChild>
          <Link to={ROUTES.systemUsers}>取消</Link>
        </Button>
        {canWrite ? (
          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? '儲存中…' : '儲存'}
          </Button>
        ) : null}
      </div>
    </form>
  );
};

UserAccessEditor.propTypes = {
  userId: PropTypes.string.isRequired,
  accessData: PropTypes.object.isRequired,
  roleOptions: PropTypes.array.isRequired,
  catalog: PropTypes.array.isRequired,
  catalogLoading: PropTypes.bool,
  canWrite: PropTypes.bool.isRequired,
};

const UserAccessPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const loadedUserId = (searchParams.get('userId') || '').trim();

  const canReadAccount = useCan(PERM.SYS_ACCOUNT_READ);
  const canReadRole = useCan(PERM.SYS_ROLE_READ);
  const canWriteAccount = useCan(PERM.SYS_ACCOUNT_UPDATE);
  const canWriteRole = useCan(PERM.SYS_ROLE_UPDATE);
  const canLoad = canReadAccount && canReadRole;
  const canWrite = canWriteAccount && canWriteRole;

  const [userIdInput, setUserIdInput] = useState('');

  const accessQuery = useQuery({
    queryKey: ['system', 'user', 'access', loadedUserId],
    queryFn: () => fetchUserAccess(loadedUserId),
    enabled: Boolean(loadedUserId) && canLoad,
  });

  const rolesQuery = useQuery({
    queryKey: ['system', 'role', 'list', 'user-access'],
    queryFn: () => fetchRoleList({
      page: 1,
      pageSize: 100,
      keyword: '',
      sort: [{ id: 'code', desc: false }],
      filters: {},
    }),
    enabled: canLoad,
  });

  const catalogQuery = useQuery({
    queryKey: ['system', 'permission', 'catalog'],
    queryFn: () => fetchPermissionList({
      page: 1,
      pageSize: 500,
      keyword: '',
      sort: [{ id: 'code', desc: false }],
      filters: {},
    }),
    enabled: canLoad,
  });

  const catalog = useMemo(() => catalogQuery.data?.data ?? [], [catalogQuery.data?.data]);
  const roleOptions = rolesQuery.data?.data ?? [];
  const accessData = accessQuery.data?.data;
  const user = accessData?.user;
  const userLabel = user ? `${user.account}${user.name ? `（${user.name}）` : ''}` : '';

  function handleLoad(event) {
    event.preventDefault();
    const nextId = userIdInput.trim();
    if (!nextId) {
      toast.error('請輸入使用者 id');
      return;
    }
    setSearchParams({ userId: nextId }, { replace: true });
  }

  if (!canLoad) {
    return (
      <PageShell variant="form">
        <div className="layout-shell__body">
          <div className="sheet mx-auto flex w-full max-w-lg flex-col items-center gap-2 py-8 text-center">
            <h1 className="text-xl font-semibold">無法存取此頁面</h1>
            <p className="text-sm text-muted-foreground">需要同時具備帳號與角色的讀取權限。</p>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-5xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">
                存取設定
                {userLabel ? <span className="ml-2 text-gray-600">{userLabel}</span> : null}
              </span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.systemUsers}>返回帳號列表</Link>
            </Button>
          </div>

          {!loadedUserId ? (
            <form
              className="sheet create_edit flex flex-col gap-3 py-5"
              onSubmit={handleLoad}
            >
              <p className="text-sm text-muted-foreground">
                請從帳號管理列表的「存取設定」進入，或輸入使用者 id 載入。
              </p>
              <div className="flex flex-wrap items-start gap-2">
                <FloatingInput
                  id="userId"
                  type="text"
                  label="使用者 id"
                  value={userIdInput}
                  onChange={(event) => setUserIdInput(event.target.value)}
                  containerClassName="min-w-72 flex-1"
                  inputClassName="font-mono"
                />
                <Button type="submit" variant="outline">載入</Button>
              </div>
            </form>
          ) : accessQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : accessQuery.isError ? (
            <p className="text-sm text-destructive">
              {getErrorMessage(accessQuery.error, '載入使用者存取失敗')}
            </p>
          ) : accessData ? (
            <UserAccessEditor
              key={`${loadedUserId}-${accessQuery.dataUpdatedAt}`}
              userId={loadedUserId}
              accessData={accessData}
              roleOptions={roleOptions}
              catalog={catalog}
              catalogLoading={catalogQuery.isLoading}
              canWrite={canWrite}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default UserAccessPage;
